import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} BookAddButtonRefs
 * @property {HTMLButtonElement} button - The add button
 * @property {HTMLElement} [status] - Live region that announces the result
 */

/**
 * Adds one copy of a single-variant product to the cart from a simple card, then tells the theme
 * through its standard cart event so the cart count and drawer update as they do everywhere else.
 *
 * @extends {Component<BookAddButtonRefs>}
 */
class BookAddButtonComponent extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | undefined} */
  #request;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
  }

  /** Adds the product to the cart. */
  async add() {
    const { button, status } = this.refs;
    const variantId = this.dataset.variantId;
    if (!variantId || button.disabled) return;

    this.#request?.abort();
    this.#request = new AbortController();
    button.disabled = true;

    try {
      const sections = [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
        .filter(Boolean);
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections.join(',') }),
        signal: this.#request.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { signal: this.#request.signal }).then((r) => r.json());
      this.dispatchEvent(
        new CartAddEvent(cart, variantId, {
          source: 'book-add-button',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId,
          sections: result.sections,
        })
      );
      if (status) status.textContent = Theme.translations.added;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (status) status.textContent = error instanceof Error ? error.message : '';
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('book-add-button')) {
  customElements.define('book-add-button', BookAddButtonComponent);
}
